import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Routes, Route } from 'react-router-dom';
import { useGroupMembershipCheck } from '../graph';
import CampaignGPT from '../components/CampaignGPT/CampaignGPT';
import { UserContext } from '../utils/UserContext';
import { loginRequest } from '../authConfig';
import DrawerContent from './DrawerContent';
import { useTokenService } from '../tokenService';
import { CopilotService } from '../Service/ServerAPI';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, setAvailableDatasets, setDataset } from '../utils/store';
import {
    OverlayDrawer,
    Button,
    Title3,
    Spinner,
} from '@fluentui/react-components';
import { DismissRegular, NavigationRegular } from '@fluentui/react-icons';
import UnauthedPage from './UnauthedPage';
import { useHubManager } from '../Service/SignalR/useHubManager';
import EvaluationFeedbackBar from '../components/EvaluationFeedbackBar/EvaluationFeedbackBar';

interface MainContentProps {
    toggleSettingsDrawer: (open: boolean) => () => void;
    toggleAboutDrawer: (open: boolean) => () => void;
    toggleDebugDrawer: (open: boolean) => () => void;
    isDarkTheme: boolean;
}

const OneMemoryUI = '3e9d2b19-98f8-44f8-8bf3-7d87678599c9';
const XGSLI_ActivisionData = '6fb301df-690f-4251-9da9-523534047c25';
const XGSLI_BlizzardData = '4ed49609-6d3d-4073-8653-fef5135f55ff';
const XGSLI_CentralData_Consumers = '742c583e-fc68-4408-9da8-547321481899';

const MainContent: React.FC<MainContentProps> = ({
    toggleSettingsDrawer,
    toggleAboutDrawer,
    toggleDebugDrawer,
}) => {
    const requiredGroupIds = useMemo(() => {
        return [OneMemoryUI, XGSLI_ActivisionData, XGSLI_BlizzardData, XGSLI_CentralData_Consumers];
    }, []);

    const finGroupIds = useMemo(() => {
        return ['7f8963df-6dd0-47dd-ab32-2ce144cfd78e','6a2b81bb-c92c-45a8-b414-9519a013d060','d09cd31d-3569-4d6c-b44f-f4d43fcce551', XGSLI_ActivisionData, XGSLI_BlizzardData, XGSLI_CentralData_Consumers];
    }, []);

    const groupIds = useMemo(() => {
        return [...requiredGroupIds, ...finGroupIds];
    }, [requiredGroupIds, finGroupIds]);

    const { isMemberInGroups, loading, user } = useGroupMembershipCheck([
        ...requiredGroupIds,
        ...finGroupIds,
    ]);
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [showScreen, setShowScreen] = useState<boolean>(true);
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileFetched, setProfileFetched] = useState(false);
    const [profiledUser, setProfiledUser] = useState<typeof user>(user);
    const getAccessToken = useTokenService();
    const settings = useSelector((state: RootState) => state.settings);
    const dispatch = useDispatch();

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        // Only trigger login if MSAL is fully idle and no accounts exist.
        // Skip if there's a #code in the URL — handleRedirectPromise is still processing it.
        const hasAuthResponse = window.location.hash.includes('code=');
        if (inProgress === InteractionStatus.None && accounts.length === 0 && !hasAuthResponse) {
            instance.loginRedirect(loginRequest);
        }
    }, [inProgress, accounts.length, instance]);

    // Keep profiledUser in sync with base user object (e.g., when group membership resolves)
    useEffect(() => {
        if (user) {
            setProfiledUser(prev => {
                const updated = Object.assign(Object.create(Object.getPrototypeOf(user || {})), user);
                if (prev?.userProfile) {
                    updated.userProfile = prev.userProfile;
                }
                return updated;
            });
        }
    }, [user, loading]);

    // Fetch user profile on authentication - only once
    useEffect(() => {
        // Skip if already fetched, not authenticated, or still loading
        if (profileFetched || !isAuthenticated || loading) {
            return;
        }
        
        const fetchUserProfile = async () => {
            try {
                setProfileLoading(true);
                const copilotService = CopilotService.getInstance(getAccessToken);
                const profile = await copilotService.getUserProfile(settings.apiEndpoint);
                
                if (profile && profile.capabilities) {
                    // Add profile to user object
                    user.userProfile = {
                        userName: profile.userName,
                        publisherRole: profile.publisherRole,
                        capabilities: profile.capabilities
                    };
                    setProfiledUser(Object.assign(Object.create(Object.getPrototypeOf(user || {})), user));
                    console.log('User profile loaded:', profile.userName, 'Debug Access:', profile.capabilities.hasDebugAccess);
                } else {
                    // Fallback to safe defaults if profile fetch fails
                    console.warn('Failed to fetch user profile, using default capabilities (all access disabled)');
                    user.userProfile = {
                        userName: user.username || '',
                        publisherRole: 'Unknown',
                        capabilities: {
                            hasGeneralAccess: false,
                            hasDebugAccess: false,
                            hasFinancialDataAccess: false,
                            hasCoretextTeamAccess: false,
                            hasExportAccess: false,
                            hasAdminAccess: false
                        }
                    };
                    setProfiledUser(Object.assign(Object.create(Object.getPrototypeOf(user || {})), user));
                }
            } catch (error) {
                console.error('Error during user profile fetch:', error);
                // Set safe defaults on error
                user.userProfile = {
                    userName: user.username || '',
                    publisherRole: 'Unknown',
                    capabilities: {
                        hasGeneralAccess: false,
                        hasDebugAccess: false,
                        hasFinancialDataAccess: false,
                        hasCoretextTeamAccess: false,
                        hasExportAccess: false,
                        hasAdminAccess: false
                    }
                };
                setProfiledUser(Object.assign(Object.create(Object.getPrototypeOf(user || {})), user));
            } finally {
                setProfileLoading(false);
                setProfileFetched(true); // Mark as fetched regardless of success/failure
            }
        };
        
        fetchUserProfile();
    }, [isAuthenticated, loading, profileFetched]);

    // Fetch available datasets on startup and whenever the API endpoint changes
    useEffect(() => {
        if (!isAuthenticated || loading) return;

        const fetchDatasets = async () => {
            try {
                const copilotService = CopilotService.getInstance(getAccessToken);
                const datasets = await copilotService.getDatasets(settings.apiEndpoint);
                if (datasets && datasets.length > 0) {
                    dispatch(setAvailableDatasets(datasets));
                    // Only set default if no dataset has been persisted
                    if (!settings.dataset) {
                        const defaultDataset = datasets.find(d => d.isDefault);
                        if (defaultDataset) {
                            dispatch(setDataset(defaultDataset.name));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching datasets:', error);
            }
        };

        fetchDatasets();
    }, [isAuthenticated, loading, settings.apiEndpoint]);

    if (!isAuthenticated) {
        return <div></div>;
    }

    if (loading || profileLoading) {
        return (
            <Spinner
                size="huge"
                style={{ position: 'absolute', top: '48%', right: '48%' }}
            />
        );
    }

    const isMemberOfFinGroup = finGroupIds.some(
        (groupId) => isMemberInGroups[groupId]
    );

    // The user should be in one of the "Required Groups" to access the app
    const isMemberOfARequiredGroup = requiredGroupIds.some(
        (groupId) => isMemberInGroups[groupId]
    );

   /* if (!isMemberOfAllRequiredGroups) {
        //if (true) {
        return <UnauthedPage />;
    }*/

    const contextUser = Object.assign(Object.create(Object.getPrototypeOf(profiledUser || {})), profiledUser, { groupMembership: isMemberInGroups, hasFinAccess: isMemberOfFinGroup });
    return (
        
        <UserContext.Provider value={contextUser}>
            <EvaluationFeedbackBar />
            <div
                style={{
                    display: 'flex',
                    flexGrow: 1,
                    height: '100dvh',
                    flexDirection: windowWidth >= 1280 ? 'row' : 'column',
                    justifyContent: windowWidth> 1280 ? 'flex-start' : 'space-between',
                }}
            >
                {windowWidth >= 1280 ? (
                    <DrawerContent
                        onSettingsClick={toggleSettingsDrawer(true)}
                        onAboutClick={toggleAboutDrawer(true)}
                        onDebugClick={toggleDebugDrawer(true)}
                        windowWidth={windowWidth}
                        isAuthed={isMemberOfARequiredGroup}
                        setShowScreen={setShowScreen}
                    />
                ) : (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '60px',
                                backgroundColor: '#25282C',
                                position: 'relative',
                            }}
                        >
                            <Button
                                appearance="subtle"
                                icon={<NavigationRegular />}
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '0',
                                    margin: '16px',
                                    zIndex: 9,
                                }}
                                onClick={() => setIsNavOpen(true)}
                            ></Button>
                            <Title3>Gaming Segmentation Copilot</Title3>
                        </div>
                        <OverlayDrawer
                            size="small"
                            open={isNavOpen}
                            onOpenChange={() => setIsNavOpen(!isNavOpen)}
                            position="start"
                            style={{ overflow: 'unset' }}
                        >
                            <Button
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    right: '-20px',
                                    margin: '20px',
                                    zIndex: 9,
                                }}
                                appearance="transparent"
                                aria-label="Close"
                                icon={<DismissRegular />}
                                onClick={() => setIsNavOpen(false)}
                            />
                            <DrawerContent
                                onSettingsClick={toggleSettingsDrawer(true)}
                                onAboutClick={toggleAboutDrawer(true)}
                                onDebugClick={toggleDebugDrawer(true)}
                                windowWidth={windowWidth}
                                isAuthed={isMemberOfARequiredGroup}
                                setShowScreen={setShowScreen}
                            />
                        </OverlayDrawer>
                    </>
                )}
                <div
                    style={{
                        flexGrow: 2,
                        flexDirection: 'column',
                    }}
                >
                    <Routes>
                        <Route path="/" element={<CampaignGPT 
                        isAuthed={isMemberOfARequiredGroup} 
                        showScreen={showScreen}
                        setShowScreen={setShowScreen}
                        />} />
                    </Routes>
                </div>
            </div>
        </UserContext.Provider>
    );
};

export default MainContent;
